// Procedural mountain models: a faceted horn (hero) and a low lump (shoulder), flat-shaded, base at
// y = 0, apex at y = 1, footprint radius 1 at the base. Pure (no GL): layer.ts wraps the arrays.
//
//   apex ●               rings step down the horn; each ring is a jittered polygon whose radius
//        ╱╲              follows r(h) = (1 − h)^PEAK_PROFILE, so the footprint area above height
//       ╱▒▒╲             fraction f is (1 − f)^(2·PEAK_PROFILE): the same rule heights.ts uses to
//      ╱▒▒▒▒╲            turn the glacier fraction of a hex into a snow line (capFraction).
//   ───┴────┴───  base
//
// Ridges (lobes in the radius) and jitter keep every model a slightly different crag, and one hex
// never looks like the next: the round cones of the old massif models made a carpet.

import { PEAK_PROFILE } from '../terrain/heights.ts';

export interface PeakMesh {
  /** 3 floats per vertex, triangles as separate vertices (flat shading). */
  position: Float32Array;
  /** Linear rgb per vertex. */
  color: Float32Array;
  /** 1 on cool alpine rock, 0 on warm arid rock (the shader cools the high alpine faces). */
  alpine: Float32Array;
}

export type RockKind = 'alpine' | 'arid';

export interface PeakSpec {
  sides: number;
  /** Ring heights as a fraction of the apex, ascending from 0; the apex closes the top. */
  rings: readonly number[];
  seed: number;
  /** Radius exponent: > 1 is a horn, < 1 a blunt lump. */
  profile: number;
  ridges: number;
  ridgeAmp: number;
  jitter: number;
  /** Horizontal apex offset (footprint radii). */
  lean: number;
  rock: RockKind;
}

/** Hero horn detail levels. */
const HERO_RINGS = [0, 0.15, 0.32, 0.5, 0.67, 0.82, 0.93] as const;
const HERO_SIDES = 9;
const HERO_RIDGES = 3;
const HERO_RIDGE_AMP = 0.26;
const HERO_JITTER = 0.14;
const HERO_LEAN = 0.07;

const SHOULDER_RINGS = [0, 0.32, 0.64] as const;
const SHOULDER_SIDES = 7;
const SHOULDER_PROFILE = 0.9;
const SHOULDER_RIDGE_AMP = 0.2;
const SHOULDER_JITTER = 0.18;

const FAR_RINGS = [0, 0.42, 0.76] as const;
const FAR_SIDES = 6;

const ANGLE_JITTER = 0.16; // of a sector
const HEIGHT_JITTER = 0.03;
const RIDGE_FLOOR = 0.6; // ridge share left at the apex

// Rock tones (sRGB): dark at the foot, light on sunlit ledges.
const ROCK_TONES: Record<RockKind, readonly [dark: string, mid: string, light: string]> = {
  alpine: ['#5b5148', '#6e6e74', '#8d94a0'],
  arid: ['#6d5439', '#a08260', '#c6a97e'],
};
const FACET_SPREAD = 0.28; // per-facet brightness range
const HUE_SPREAD = 0.05;

/** The models the feature layer builds, keyed by SlotDef.file (placement.ts). */
export function peakSpec(name: string): PeakSpec | null {
  const [kind, tail] = name.split(':');
  if (kind !== 'peak' || !tail) {
    return null;
  }
  const parts = tail.split('-');
  const rock: RockKind = parts[1] === 'arid' ? 'arid' : 'alpine';
  const seed = Number(parts[2] ?? 0) * 97 + 13;
  if (parts[0] === 'hero') {
    return { sides: HERO_SIDES, rings: HERO_RINGS, seed, profile: PEAK_PROFILE, ridges: HERO_RIDGES, ridgeAmp: HERO_RIDGE_AMP, jitter: HERO_JITTER, lean: HERO_LEAN, rock };
  }
  if (parts[0] === 'shoulder') {
    return { sides: SHOULDER_SIDES, rings: SHOULDER_RINGS, seed, profile: SHOULDER_PROFILE, ridges: 2, ridgeAmp: SHOULDER_RIDGE_AMP, jitter: SHOULDER_JITTER, lean: HERO_LEAN, rock };
  }
  if (parts[0] === 'far') {
    return { sides: FAR_SIDES, rings: FAR_RINGS, seed, profile: PEAK_PROFILE, ridges: 2, ridgeAmp: HERO_RIDGE_AMP, jitter: HERO_JITTER, lean: HERO_LEAN, rock };
  }
  return null;
}

/** Small deterministic random source (mulberry32). */
function source(seed: number): () => number {
  let a = seed >>> 0;
  return () => {
    a = (a + 0x6d2b79f5) >>> 0;
    let t = a;
    t = Math.imul(t ^ (t >>> 15), t | 1);
    t ^= t + Math.imul(t ^ (t >>> 7), t | 61);
    return ((t ^ (t >>> 14)) >>> 0) / 4294967296;
  };
}

const SRGB_LINEAR_KNEE = 0.04045;

function linear(hex: string): [number, number, number] {
  const v = parseInt(hex.slice(1), 16);
  const ch = (shift: number): number => {
    const c = ((v >> shift) & 0xff) / 255;
    return c <= SRGB_LINEAR_KNEE ? c / 12.92 : Math.pow((c + 0.055) / 1.055, 2.4);
  };
  return [ch(16), ch(8), ch(0)];
}

/** One ring: the polygon (x, z) at height fraction `h`. */
export interface Ring {
  h: number;
  points: Array<[number, number]>;
}

/** The rings and the apex of a model, before triangulation. */
export function peakRings(spec: PeakSpec): { rings: Ring[]; apex: [number, number, number] } {
  const rand = source(spec.seed);
  const phase = rand() * Math.PI * 2;
  const leanAngle = rand() * Math.PI * 2;
  const angles: number[] = [];
  for (let k = 0; k < spec.sides; k++) {
    angles.push(((k + (rand() - 0.5) * 2 * ANGLE_JITTER) / spec.sides) * Math.PI * 2);
  }
  const sway = Array.from({ length: spec.sides }, () => 1 + (rand() - 0.5) * 2 * spec.jitter);

  const rings: Ring[] = spec.rings.map((h, j) => {
    const amp = spec.ridgeAmp * (RIDGE_FLOOR + (1 - RIDGE_FLOOR) * (1 - h));
    const dh = j === 0 ? 0 : (rand() - 0.5) * 2 * HEIGHT_JITTER;
    const points = angles.map((a, k): [number, number] => {
      const radius = Math.pow(1 - h, spec.profile) * (1 + amp * Math.cos(spec.ridges * (a - phase))) * sway[k];
      const offset = spec.lean * h * h;
      return [Math.cos(a) * radius + Math.cos(leanAngle) * offset, Math.sin(a) * radius + Math.sin(leanAngle) * offset];
    });
    return { h: Math.max(0, h + dh), points };
  });
  return { rings, apex: [Math.cos(leanAngle) * spec.lean, 1, Math.sin(leanAngle) * spec.lean] };
}

/** Cross-section area of a model at height fraction `f`, as a share of its base area. */
export function crossSection(spec: PeakSpec, f: number): number {
  const { rings } = peakRings(spec);
  const area = (points: ReadonlyArray<readonly [number, number]>): number => {
    let sum = 0;
    for (let k = 0; k < points.length; k++) {
      const a = points[k];
      const b = points[(k + 1) % points.length];
      sum += a[0] * b[1] - b[0] * a[1];
    }
    return Math.abs(sum) / 2;
  };
  const base = area(rings[0].points);
  const top = rings[rings.length - 1];
  if (f >= top.h) {
    // Between the last ring and the apex the section shrinks to a point: scale the last ring.
    const share = (1 - f) / (1 - top.h);
    return (area(top.points) * share * share) / base;
  }
  let j = 0;
  while (rings[j + 1].h < f) {
    j++;
  }
  const t = (f - rings[j].h) / (rings[j + 1].h - rings[j].h);
  const mixed = rings[j].points.map((p, k): [number, number] => [p[0] + (rings[j + 1].points[k][0] - p[0]) * t, p[1] + (rings[j + 1].points[k][1] - p[1]) * t]);
  return area(mixed) / base;
}

/** Triangulate a model: flat facets with outward winding, tone by height and a per-facet jitter. */
export function peakMesh(spec: PeakSpec): PeakMesh {
  const { rings, apex } = peakRings(spec);
  const rand = source(spec.seed + 1);
  const tones = ROCK_TONES[spec.rock].map(linear);
  const alpine = spec.rock === 'alpine' ? 1 : 0;
  const position: number[] = [];
  const color: number[] = [];

  const toneAt = (h: number): [number, number, number] => {
    const lower = h < 0.5;
    const from = lower ? tones[0] : tones[1];
    const to = lower ? tones[1] : tones[2];
    const t = lower ? h * 2 : (h - 0.5) * 2;
    return [from[0] + (to[0] - from[0]) * t, from[1] + (to[1] - from[1]) * t, from[2] + (to[2] - from[2]) * t];
  };

  const facet = (a: number[], b: number[], c: number[]): void => {
    const cx = (a[0] + b[0] + c[0]) / 3;
    const cz = (a[2] + b[2] + c[2]) / 3;
    const cy = (a[1] + b[1] + c[1]) / 3;
    const nx = (b[1] - a[1]) * (c[2] - a[2]) - (b[2] - a[2]) * (c[1] - a[1]);
    const nz = (b[0] - a[0]) * (c[1] - a[1]) - (b[1] - a[1]) * (c[0] - a[0]);

    // Outward: the horizontal part of the normal points away from the axis.
    const outward = nx * cx + nz * cz >= 0;
    const tri = outward ? [a, b, c] : [a, c, b];
    const tone = toneAt(cy);
    const shade = 1 + (rand() - 0.5) * 2 * FACET_SPREAD;
    const hue = (rand() - 0.5) * 2 * HUE_SPREAD;
    for (const p of tri) {
      position.push(p[0], p[1], p[2]);
      color.push(tone[0] * shade * (1 + hue), tone[1] * shade, tone[2] * shade * (1 - hue));
    }
  };

  const at = (j: number, k: number): number[] => [rings[j].points[k][0], rings[j].h, rings[j].points[k][1]];
  const n = spec.sides;
  for (let j = 0; j + 1 < rings.length; j++) {
    for (let k = 0; k < n; k++) {
      const k1 = (k + 1) % n;
      facet(at(j, k), at(j, k1), at(j + 1, k1));
      facet(at(j, k), at(j + 1, k1), at(j + 1, k));
    }
  }
  const top = rings.length - 1;
  for (let k = 0; k < n; k++) {
    facet(at(top, k), at(top, (k + 1) % n), [apex[0], apex[1], apex[2]]);
  }

  return { position: new Float32Array(position), color: new Float32Array(color), alpine: new Float32Array(position.length / 3).fill(alpine) };
}
